import React from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Overline, OverlineSize, Span } from '../Texts/Texts';
import { Badge } from '../Badge/Badge';
import { IGutterAsMarginProps } from '../../fundamentals/spaces/space';
import styled, { css } from 'styled-components';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { FontSize, FONT_SIZES, FONT_WEIGHTS, FontWeight } from '../../fundamentals/typography/typography';
import { MATERIAL_ICONS, MaterialIcon } from '../Icon/icons';
import { useCenteredFlexCss } from '../../fundamentals/display';
import { DEFAULT_DECIMAL_PRECISION } from '../../common/constants';
import { STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { INumericProps, Numeric } from '../../fundamentals/numeric/Numeric';
import { TriangleIndicator, TriangleIndicatorSize } from '../TriangleIndicator/TriangleIndicator';

export const PRICE_MOVEMENT = {
  UP: 'Up',
  DOWN: 'Down',
  NO_CHANGE: 'NoChange',
  UNKNOWN: 'Unknown'
} as const;

export type PriceMovement = (typeof PRICE_MOVEMENT)[keyof typeof PRICE_MOVEMENT];
export type DeltaSize = typeof SIZES.XX_SMALL | typeof SIZES.X_SMALL | typeof SIZES.SMALL;

export interface IDeltaProps extends IGutterAsMarginProps, Omit<INumericProps, 'value'> {
  priceChange?: number; // same params with module-instrument
  priceChangePercent?: number; // same params with module-instrument
  priceMovement?: PriceMovement | number; // same params with module-instrument
  showIcon?: boolean;
  type?: 'text' | 'textColoured' | 'badge' | 'badgeLight';
  deltaSize?: DeltaSize;
  deltaPrefix?: string;
  /** DEPRECATED, this is ignored */
  reverse?: boolean; // TODO this is deprecated, remove once calling code has it removed
  footer?: boolean;
  noWrap?: boolean;

  animate?: boolean; // no action yet

  // Numeric params for priceChangePercent
  priceChangePercentFractionDigits?: number; // overwrites `fractionDigits` for `priceChangePercent`,
  priceChangePercentPrefix?: string; // overwrites `prefix` for `priceChangePercent`,
  priceChangePercentSuffix?: string; // overwrites `prefix` for `priceChangePercent`,
  hidePlusInPercent?: boolean;
  overlineSize?: OverlineSize;
}

const deltaFontSizes: Record<DeltaSize, FontSize> = {
  [SIZES.XX_SMALL]: FONT_SIZES.TEXT_SMALL,
  [SIZES.X_SMALL]: FONT_SIZES.TEXT_DEFAULT,
  [SIZES.SMALL]: FONT_SIZES.HEADING_5
};

type DeltaVariant = typeof STATUS_COLOURS.SUCCESS | typeof STATUS_COLOURS.ERROR;
type IDeltaCssProps = IDeltaProps & { movement?: PriceMovement };

const DeltaSpan = styled(Span)<Pick<IDeltaProps, 'noWrap'>>`
  ${useCenteredFlexCss};
  column-gap: ${PX_TO_REM[4]};
  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
    `}
`;

const useTextColoured = css<IDeltaCssProps>`
  ${({ movement, theme, type }) =>
    (movement === PRICE_MOVEMENT.UP || movement === PRICE_MOVEMENT.DOWN) &&
    type === 'textColoured' &&
    css`
      color: ${theme.colours.trading[movement === PRICE_MOVEMENT.UP ? 'positive' : 'negative']};
    `}
`;

const DeltaText = styled(Span)<IDeltaCssProps>`
  ${useTextColoured};
`;

const DeltaOverline = styled(Overline)<IDeltaCssProps>`
  ${useTextColoured};
`;

const movementMap: Record<PriceMovement, { icon?: MaterialIcon; variant?: DeltaVariant }> = {
  [PRICE_MOVEMENT.UP]: {
    icon: MATERIAL_ICONS.DeltaUp,
    variant: STATUS_COLOURS.SUCCESS
  },
  [PRICE_MOVEMENT.DOWN]: {
    icon: MATERIAL_ICONS.DeltaDown,
    variant: STATUS_COLOURS.ERROR
  },
  [PRICE_MOVEMENT.NO_CHANGE]: {},
  [PRICE_MOVEMENT.UNKNOWN]: {}
};

const deltaAsTextIconSizes: Record<DeltaSize, TriangleIndicatorSize> = {
  [SIZES.XX_SMALL]: SIZES.MICRO,
  [SIZES.X_SMALL]: SIZES.XXX_SMALL,
  [SIZES.SMALL]: SIZES.XX_SMALL
};

const deltaSpanFontWeights: Record<DeltaSize, FontWeight> = {
  [SIZES.XX_SMALL]: FONT_WEIGHTS.REGULAR,
  [SIZES.X_SMALL]: FONT_WEIGHTS.MEDIUM,
  [SIZES.SMALL]: FONT_WEIGHTS.MEDIUM
};

export const getPriceMovement = ({
  priceMovement,
  priceChangePercent,
  priceChange
}: Pick<IDeltaProps, 'priceMovement' | 'priceChange' | 'priceChangePercent'>) => {
  if (priceMovement && typeof priceMovement !== 'number') return priceMovement;

  // Priority
  // 1. priceMovement
  // 2. priceChange
  // 3. priceChangePercent
  if (((priceMovement ?? priceChange ?? priceChangePercent!) as number) > 0) {
    return PRICE_MOVEMENT.UP;
  } else if (((priceMovement ?? priceChange ?? priceChangePercent!) as number) < 0) {
    return PRICE_MOVEMENT.DOWN;
  }

  return PRICE_MOVEMENT.NO_CHANGE;
};

export const Delta: React.FC<IDeltaProps> = ({
  type = 'text',
  priceChangePercent,
  priceChange,
  deltaSize = SIZES.X_SMALL,
  showIcon,
  priceMovement,
  fractionDigits = DEFAULT_DECIMAL_PRECISION,
  priceChangePercentFractionDigits,
  priceChangePercentPrefix,
  priceChangePercentSuffix,
  hidePlusInPercent,
  deltaPrefix,
  footer = false,
  noWrap = false,

  // Passed to <Numeric /> for priceChange
  prefix,
  suffix,
  thousandsAbbreviation,
  millionsAbbreviation,
  locale,
  minimumFractionDigits,
  abbreviate,
  showPositive,
  showNegativeParentheses,
  zeroIsNoValue,
  overlineSize = FONT_SIZES.TEXT_DEFAULT,
  ...rest
}) => {
  if (priceChange === undefined && priceChangePercent === undefined && !priceMovement && !showIcon) {
    return <></>;
  }

  const { generateTestId } = useTestId(rest, 'Delta');

  const movement = getPriceMovement({ priceMovement, priceChangePercent, priceChange });

  const { icon, variant } = movementMap[movement];
  const priceChangeNumericProps = {
    thousandsAbbreviation,
    millionsAbbreviation,
    locale,
    minimumFractionDigits: minimumFractionDigits ?? fractionDigits,
    abbreviate,
    showPositive,
    showNegativeParentheses,
    zeroIsNoValue
  };

  const hasBothPrices = priceChangePercent !== undefined && priceChange !== undefined;

  const PriceChangePercent: React.FC<{ addParentheses: boolean }> = ({ addParentheses }) => {
    if (priceChangePercent === undefined) return <></>;
    return (
      <Numeric
        value={priceChangePercent}
        fractionDigits={priceChangePercentFractionDigits ?? fractionDigits}
        minimumFractionDigits={priceChangePercentFractionDigits ?? fractionDigits}
        showPositive={!hidePlusInPercent}
        prefix={`${addParentheses ? '(' : ''}${priceChangePercentPrefix ?? ''}`}
        suffix={`%${priceChangePercentSuffix ?? ''}${addParentheses ? ')' : ''}`}
      />
    );
  };

  const PriceChangeAmount: React.FC<{ addParentheses: boolean }> = ({ addParentheses }) => {
    if (priceChange === undefined) return <></>;
    return (
      <Numeric
        value={priceChange}
        fractionDigits={fractionDigits}
        prefix={addParentheses ? /* istanbul ignore next */ `(${prefix ?? /* istanbul ignore next */ ''}` : prefix}
        suffix={addParentheses ? /* istanbul ignore next */ `${suffix ?? /* istanbul ignore next */ ''})` : suffix}
        {...priceChangeNumericProps}
      />
    );
  };

  const PriceChange = () => (
    <>
      <PriceChangeAmount addParentheses={false} />
      {hasBothPrices && ' '}
      <PriceChangePercent addParentheses={hasBothPrices} />
    </>
  );

  if (type.startsWith('badge')) {
    return (
      <Badge
        variant={variant}
        light={type === `badgeLight`}
        prefix={deltaPrefix}
        startIcon={showIcon && icon ? icon : undefined}
        label={<PriceChange />}
        size={deltaSize}
        {...rest}
        {...generateTestId(`badge`)}
      />
    );
  }

  return (
    <DeltaSpan noWrap={noWrap} {...generateTestId()} {...rest}>
      {deltaPrefix &&
        (footer ? (
          <DeltaOverline size={overlineSize} movement={movement} type={type}>
            {deltaPrefix}
          </DeltaOverline>
        ) : (
          <DeltaText textSize={deltaFontSizes[deltaSize]} fontWeightCss={deltaSpanFontWeights[deltaSize]}>
            {deltaPrefix}
          </DeltaText>
        ))}
      {showIcon && icon && (
        <TriangleIndicator negative={movement === PRICE_MOVEMENT.DOWN} size={deltaAsTextIconSizes[deltaSize]} {...generateTestId('TriangleIndicator')} />
      )}
      {footer ? (
        <DeltaOverline size={overlineSize} movement={movement} type={type}>
          <PriceChange />
        </DeltaOverline>
      ) : (
        <DeltaText
          textSize={deltaFontSizes[deltaSize]}
          {...generateTestId('priceChange')}
          movement={movement}
          fontWeightCss={deltaSpanFontWeights[deltaSize]}
          type={type}
        >
          <PriceChange />
        </DeltaText>
      )}
    </DeltaSpan>
  );
};
